import {
    takeEvery,
    all,
    put,
    call,
    select,
    fork
} from 'redux-saga/effects';
import moment from 'moment';
import fp from 'lodash/fp';
import * as pricingParametersApi from '../../api/pricingParamsApi';
import * as tradePricedTodayApi from '../../api/tradePricedTodayApi';
import * as actions from './actions';
import * as selectors from './selectors';
import * as summarySelectors from '../impactAnalysisIdSummary/selectors';
import * as appConstants from '../../common/constants';
import { loadTradesIntoPricingParams } from '../../api/adapters/pricingParametersSubmit';

export function* saveAsTemplate(api, action) {
    try {
        yield put(actions.setTemplateInProcessOfSaving(true));
        yield call(api.saveTemplate, {
            pricingParameters: action.templateData,
            templateName: action.name
        });
        yield put(actions.setTemplateSaved(true));
        const templates = yield call(api.loadTemplatesNames);
        yield put(actions.setTemplates(templates));
    } catch (error) {
        yield put(actions.saveAsTemplateFailed(error.message));
    } finally {
        yield put(actions.setTemplateInProcessOfSaving(false));
    }
}

export function* loadTemplatesNames(api) {
    const templatesAlreadyLoaded = yield select(selectors.getTemplatesNamesLoaded);
    if (!templatesAlreadyLoaded) {
        try {
            const templates = yield call(api.loadTemplatesNames);
            yield put(actions.setTemplates(templates));
            yield put(actions.setTemplatesNamesLoaded(true));
        } catch (error) {
            yield put(actions.getTemplatesNamesFailed(error.mesage));
        }
    }
}

export function* fetchIndividualTemplate(api, action) {
    const individualTemplate = yield call(api.loadIndividualTemplate, action.templateId);
    yield put(actions.loadSelectedRows(individualTemplate));
}

export function* deleteTemplate(api, action) {
    yield call(api.deleteTemplate, action.templateId);
    const templates = yield call(api.loadTemplatesNames);
    yield put(actions.setTemplates(templates));
}


export function* togglePricingParameters(api, action) {
    const id =  yield select(selectors.getSelectedViewImModel);
    const imModelPricingParams = yield select(selectors.getImModelPricingParameters);
    const toggled = id === 'STD' ? imModelPricingParams.alternativePricingParameters : imModelPricingParams.pricingParameters
    toggled === null ? fp.noop() : yield put(actions.loadSelectedRows(toggled));

}

export function* pricingParametersLoaded(api) {
    yield put(actions.setPricingParametersRows([]));
    yield put(actions.addPricingParametersRow());
    yield fork(loadTemplatesNames, api);
}

const formatNumber = (num, digits = 0) => (parseInt(num * (10 ** digits))
 / (10 ** digits)).toString();

export function* setUsdAmount(convertToUsd, currency, amount, rowId) {
    const date = yield select(selectors.getDateFrom);
    const newFxRates = yield select(selectors.getFxRates);
    const exchangeReq = newFxRates[date];
    if (exchangeReq && exchangeReq.length) {
        const ratesObj = exchangeReq.find(x => x.ccy === currency);
        if (ratesObj) {
            if (convertToUsd) {
                const amountUsd = amount / ratesObj.usdfxRate;
                yield put(actions.setActiveValue('amountUsd', formatNumber(amountUsd, 8), rowId));
            } else {
                const amountNotUsd = amount * ratesObj.usdfxRate;
                yield put(actions.setActiveValue('amount', formatNumber(amountNotUsd, 8), rowId));
            }
        }
    } else {
        // No Exchange rates for this month action here
    }
}

export function* selectAmountCurrency(action) {
    if (action.key === 'amountCcy') {
        const rows = yield select(selectors.getRows);
        const { amountUsd } = rows.find(row => row.id === action.rowId);
        if (amountUsd !== undefined && amountUsd !== '') {
            yield setUsdAmount(false,
                action.value, amountUsd, action.rowId);
        }
    }
}

export function* setDefaultBucket(api, action) {
    try {
        const defaultBucket = yield call(api.getDefaultBucket, action.risk, action.qualifier);
        yield put(actions.setActiveValue('bucket', defaultBucket, action.rowId));
    } catch (error) {
        yield put(actions.getDefaultBucketFailed(error.message));
    }
}

export function* importSensitivities(api, action) {
    try {
        yield put(actions.setLoading(true));
        const dateFrom = yield select(selectors.getDateFrom);
        const activeCsa = yield select(selectors.getActiveCsa);
        const request = {
            csa: activeCsa,
            runDate: moment(dateFrom).format(appConstants.dateFormat),
            senstivityFile: action.file
        };
        const sensitivities = yield call(api.importSensitivities, request);
        if (sensitivities.error) {
            yield put(actions.importSensitivitiesFailed(sensitivities.error));
            yield put(actions.removeAllRows());
            yield put(actions.addPricingParametersRow());
        } else {
            yield put(actions.setImMmodelPricingParams( 
                {
                pricingParameters: sensitivities.pricingParameters,
                alternativePricingParameters: sensitivities.alternativePricingParameters
                }))
            yield put(actions.loadSelectedRows(sensitivities.pricingParameters));
        }
    } catch (error) {
        yield put(actions.importSensitivitiesFailed(error.message));
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* deriveAmountUsd(api, action) {
    try {
        const date = yield select(selectors.getDateFrom);
        const originalFxRates = yield select(selectors.getFxRates);
        if (originalFxRates[date] === undefined) {
            const fxRates = yield call(api.fxRates, moment(date).format(appConstants.dateFormat));
            yield put(actions.setFxRates(fxRates, date));
        }
        yield setUsdAmount(action.convertToUsd,
            action.currency, action.amount, action.rowId);
    } catch (error) {
        yield put(actions.setFxRatesfailed(error.message));
    }
}


export function* loadTradeIntoPricingParams(api, action) {
    if (action.readOnly) {
        const pricingParameters = yield select(summarySelectors.getPricingParams);
        const { id } = action.trade;
        const rows = fp.flow(
            fp.filter(row => id === row.tradeID),
            fp.map(row => fp.set('readOnly', true)(row)),
            loadTradesIntoPricingParams
        )(pricingParameters);
        yield put(actions.loadSelectedRows(rows));
    } else {
        const dateFrom = yield select(selectors.getDateFrom);
        const activeCsa = yield select(selectors.getActiveCsa);
        try {
            const tradeRows = yield call(api.getTradeInfo, action.trade.id, moment(dateFrom).format(appConstants.dateFormat), activeCsa);
            yield put(actions.loadSelectedRows(tradeRows));
        } catch (error) {
            yield put(actions.loadSelectedRowsFailed(error.message));
        }
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(actions.PRICING_PARAMETERS_LOADED, pricingParametersLoaded, pricingParametersApi),
        takeEvery(actions.SAVE_AS_TEMPLATE, saveAsTemplate, pricingParametersApi),
        takeEvery(actions.SELECT_TEMPLATE, fetchIndividualTemplate, pricingParametersApi),
        takeEvery(actions.DERIVE_AMOUNT_USD, deriveAmountUsd, pricingParametersApi),
        takeEvery(actions.SET_ACTIVE_VALUE, selectAmountCurrency),
        takeEvery(actions.GET_DEFAULT_BUCKET, setDefaultBucket, pricingParametersApi),
        takeEvery(actions.VIEW_TRADE_IN_PRICING_PARAMS,
            loadTradeIntoPricingParams, tradePricedTodayApi),
        takeEvery(actions.DELETE_TEMPLATE, deleteTemplate, pricingParametersApi),
        takeEvery(actions.IMPORT_SENSITIVITIES, importSensitivities, pricingParametersApi),
        takeEvery(actions.TOGGLE_PRICING_PARAMETERS, togglePricingParameters, pricingParametersApi )
    ]);
}
